<script>
    import Assessment from "./Models/assessmentModel";
    import {onMount} from 'svelte';
    import {session} from './Utilities/Session.svelte';
    import {confirm} from './Components/Confirmation.svelte';
    import {clean} from './Utilities/Cleaner.svelte';
    import AssessmentFilter from './AssessmentFilter.svelte';
    import Question from './Components/Question.svelte';
    import QuestionTags from './Components/QuestionTags.svelte';

    import Notes from './Components/Notes.svelte';
    import PreviousNotes from './Components/PreviousNotes.svelte';

    import Popup from './Components/Popup.svelte';
    import AssessorResultTags from './Components/AssessorResultTags.svelte';
    import IntersectObserve from './Utilities/IntersectObserve.svelte';
    import Notepad from './Components/Notepad.svelte';
    import Loader from './Components/Loader.svelte';

    import Highlight from './Components/Highlight.svelte';

    import QuestionTypes from './lib/QuestionTypes';

    import Fa from "svelte-fa";
    import {faNoteSticky} from "@fortawesome/free-regular-svg-icons"


    import { io } from "socket.io-client";

    const socket = io();

    export let params;

    let controlY = 0;

    let selectedControl;
    let levelFilter;

    let hiddenControls = {}

    let controlElems = {};
    let controlDetailElem;

    let debounceTimer;

    let controlScroll = "";

    let assessment_model;
    let assessment;

    let questions = [];

    let topicTagFilter = [];

    let loading = true;

    let scratchPadVisible = false;

    let noteCache = {};

    let searchText = "";


    onMount(async () => {
        let assessment_details = await Assessment.getAssessmentDetails(params.id);

        assessment_model = new Assessment(assessment_details);

        let unsub = assessment_model.store.subscribe((data) => {
            // data is undefined at start
            if (!data) return;

            // handle assessment scratch notes
            if (!data.scratch_notes[$session.user.email]) {
                data.scratch_notes[$session.user.email] = {}
            }

            // set assessment data
            assessment = data;
            questions = assessment.assessment_type_data.categories;

            if (!controlScroll) {
                for (let q of questions) {
                    for (let s of q.subcategories) {
                        for (let c of s.controls) {
                            controlScroll = c.id
                            break;
                        }
                        if (controlScroll) break
                    }
                    if (controlScroll) break
                }
            }

            if (!levelFilter) levelFilter = assessment.details.level;
            loading = false;
        });

        socket.on("connect", () => {
            socket.emit("join", {
                room: params.id,
                email: $session.user.email
            });
        });

        return () => {
            socket.emit("broadcast", {
                room: params.id,
                email: $session.user.email,
                type: "AssessorLeave"
            });

            unsub();
        }
    });


    function uploadEvidence(evt) {
        let form = evt.target;

        if (!(form.evidence_file.files.length || form.evidence_description.value)) {
            alert("You must attach a file or add a description");
            return;
        }

        if (form.evidence_file.files.length) {
            for (let file of form.evidence_file.files) {
                assessment_model.uploadEvidence(form.control_id.value, form.evidence_description.value, file)
            }
        } else {
            assessment_model.uploadEvidence(form.control_id.value, form.evidence_description.value);
        }

        form.reset();
        form.evidence_file.nextElementSibling.nextElementSibling.innerText = "Select File...";
    }

    function deleteEvidence(attachment) {
        confirm(`Are you sure you want to delete "${attachment.filename || attachment.description}"?`).then(() => {
            assessment_model.deleteEvidence(attachment);
        }).catch(() => null);
    }


    async function scrollToControl(id) {
        controlDetailElem.scrollTo(0, controlElems[id].offsetTop) // - window.innerHeight / 4);
        setControlScroll(id);
    }

    function checkScroll(id, controlY)
    {
        let control_elem = controlElems[id];
        if (!control_elem) return false

        return (controlY > control_elem.offsetTop - 21 && controlY < control_elem.offsetTop + control_elem.clientHeight + 20)
    }

   function debounce(func, ms) {
        if (!ms) ms = 100;

        if (debounceTimer) clearTimeout(debounceTimer);

        debounceTimer = setTimeout(func, ms);
    }

    function setControlScroll(control_id) {
        if (controlScroll != control_id) {
            socket.emit("broadcast", {
                room: params.id,
                type: 'assessorControl',
                email: $session.user.email,
                'control_id': control_id
            })
        }

        controlScroll = control_id
    }

    async function updateControlScroll(evt) {
        debounce(() => {
            controlY = evt.target.scrollTop;

            for (let control_id in controlElems) {
                if (checkScroll(control_id, controlY)) {
                    setControlScroll(control_id)
                    break
                }
            }
        })
    }

    function toggleTopicTagFilter(tag) {
        if (topicTagFilter.indexOf(tag) == -1) {
            topicTagFilter = [...topicTagFilter, tag];
        } else {
            topicTagFilter = topicTagFilter.filter(t => t != tag);
        }
    }

    function dragEnter(evt, control) {
        evt.target.classList.add('has-background-primary-light');
        return false;
    }

    function dragLeave(evt, control) {
        evt.target.classList.remove('has-background-primary-light');
    }

    function dragDrop(evt, control) {
        let domain = evt.dataTransfer.getData("Text");

        assessment_model.mapDomain(control.id, domain);

        evt.target.classList.remove('has-background-primary-light');
    }
</script>

<Loader loading={loading}>
<div class="columns viewport">
    <div class="column is-3">
        <AssessmentFilter
            {assessment}
            {questions}
            {assessment_model}
            {levelFilter}
            {controlScroll}
            {socket}
            bind:searchText
            bind:topicTagFilter
            bind:hiddenControls = {hiddenControls}
            on:controlClick={(evt) => {
                scrollToControl(evt.detail.control.id)
            }}
        >
        </AssessmentFilter>
    </div>
    <div class="column p0" bind:this={controlDetailElem} on:scroll={updateControlScroll}>
        {#each questions as category (category.id)}
        <div class:is-hidden={hiddenControls[category.id]}>
            <h1 class="title is-2">
                <Highlight term={searchText} text={category.name} />
            </h1>
            {#each category.subcategories as subcategory (subcategory.id)}
            <div class:is-hidden={hiddenControls[subcategory.id]}>
                <h1 class="title is-3">
                    <Highlight term={searchText} text="{subcategory.control_number} {subcategory.name}" />
                </h1>
                {#each subcategory.controls as control (control.id)}

                <div class:is-hidden={hiddenControls[control.id]}>
                    <div class="content box" bind:this={controlElems[control.id]} on:click={() => setControlScroll(control.id)}
                        on:dragenter|self|capture|stopPropagation={(evt) => dragEnter(evt, control)}
                        on:dragover|self|capture|preventDefault={()=>  false}
                        on:dragleave|self|capture|stopPropagation={dragLeave}
                        on:drop|stopPropagation|preventDefault={(evt) => dragDrop(evt,control)}
                    >
                        <IntersectObserve elHeight=450 elWidth=0>
                        <!-- Title And Evidence Button-->
                        <h1 class="title is-4">
                            {#if assessment.evidence[control.id]}
                            <span
                                class="button is-pulled-right"
                                on:click={() => selectedControl = control}
                            >
                                Manage Evidence ({(assessment.evidence[control.id] || []).length})
                            </span>
                            {/if}
                            {control.control_number} <Highlight term={searchText} text={control.name || ""} />
                        </h1>

                        <!-- Control Level-->
                        <h2 class="title is-5">
                            Level {control.level}
                        </h2>

                        {#if control.text !== control.name}
                        <div class="control-text">
                            <Highlight term={searchText} text={control.text} />
                        </div>
                        {/if}

                        <div class="columns">
                            <div class="column">
                                <!-- Overall Score -->
                                <div class="clear">
                                    <div class="field">
                                        <Question control={control} model={assessment_model} assessment={assessment} assessor={$session.user.email} />
                                    </div>
                                    {#if control.question_type != "free_text"}
                                    <div class="field is-grouped is-grouped-multiline">
                                         <!-- Assessor Results -->
                                        {#each Object.keys(assessment.assessor_results) as assessor}
                                            {#if assessor != $session.user.email}
                                                <AssessorResultTags {control} {assessor} {assessment} model={assessment_model}></AssessorResultTags>
                                            {/if}
                                        {/each}
                                        <AssessorResultTags {control} {assessment} model={assessment_model}></AssessorResultTags>

                                    </div>
                                    {/if}
                                </div>
                                {#if (assessment.domains.mapping[control.id] || []).length}
                                    {#each assessment.domains.mapping[control.id].sort() as domain}
                                        <div class="clear">
                                            <hr class="bold">
                                            <h2 class='title is-4'>{domain}</h2>
                                            <div class="field">
                                                <Question control={control} model={assessment_model} assessment={assessment} assessor={$session.user.email} domain={domain} />
                                            </div>

                                            {#if control.question_type != "free_text"}
                                            <div class="field is-grouped is-grouped-multiline">
                                                <!-- Assessor Results -->
                                                {#each Object.keys(assessment.assessor_results) as assessor}
                                                    {#if assessor != $session.user.email}
                                                        <AssessorResultTags {control} {assessor} {assessment} {domain} model={assessment_model}></AssessorResultTags>
                                                    {/if}
                                                {/each}

                                                <AssessorResultTags {control} {assessment} {domain} model={assessment_model}></AssessorResultTags>

                                            </div>
                                            {/if}
                                        </div>
                                    {/each}
                                {/if}
                                <div class="clear">
                                    <hr class="bold">
                                </div>

                                <!-- Topics -->
                                {#if control.topic_tags}
                                <div>
                                    <h3 class="title is-5">Topics</h3>
                                    <div class="tags">
                                    {#each control.topic_tags as tag}
                                        <span class="tag is-medium pointer" on:click={() => toggleTopicTagFilter(tag)} class:is-info={topicTagFilter.includes(tag)}>{tag}</span>
                                    {/each}
                                    </div>
                                </div>
                                {/if}

                                <!-- References
                                <div>
                                    <h3 class="title is-5">References</h3>
                                    <div class="tags">
                                    {#each control.references as reference}
                                        <span class="tag">{reference.framework} {reference.control}</span>
                                    {/each}
                                    </div>
                                </div>

                                <hr>
                                -->

                                <!-- Guidance-->
                                {#if control.guidance}
                                <div class="guidance_container">
                                    <input type="checkbox" class="collapse" id="guidance_{control.id}">
                                    <label for="guidance_{control.id}" class="title is-5">Guidance</label>
                                    <div class="collapsible guidance">
                                        {@html clean(control.guidance)}
                                    </div>
                                </div>
                                {/if}
                            </div>

                            <div class="column">
                                <!-- Question Tags-->
                                <div class="buttons is-right">
                                    <QuestionTags control={control} assessment={assessment} />
                                </div>

                                <!-- Notes-->
                                <div>
                                    <Notes {assessment} {control} {searchText} bind:cache={noteCache} />
                                </div>

                            </div>
                        </div>
                        </IntersectObserve>
                    </div>
                    <hr>
                </div>
                {/each}
            </div>
            {/each}
        </div>
        {/each}
        {#if assessment.orphaned_controls.controls.length}
            <div>
                <h1 class="title is-2">Orphaned Controls</h1>
                {#each assessment.orphaned_controls.controls as ctrl}
                    {@const control = ctrl.control}
                    <div class="content box" bind:this={controlElems[control.id]}>
                        <div>
                        <!-- Title And Evidence Button-->
                        <h1 class="title is-4">
                                {control.control_number} {control.name || ""}
                            </h1>

                            <!-- Control Level-->
                            <h2 class="title is-5">
                                Level {control.level}
                            </h2>

                            {#if control.text !== control.name}
                                <div class="control-text">
                                    {control.text}
                                </div>
                            {/if}
                        </div>

                        <div class="columns">
                            <div class="column">
                                <!-- Overall Score -->
                                <div class="clear">
                                    <div class="field">
                                        {#if control.question_type == "free_text"}
                                            <span class="tag">{ctrl.value}</span>
                                        {:else}
                                        {#each Object.values(QuestionTypes[control.question_type || "implementation_status"].buttons) as btn}
                                            {#if ctrl.value == btn.value}
                                                <button class="button {btn.color}">
                                                    {btn.label}
                                                </button>
                                            {/if}
                                        {/each}
                                        {/if}
                                    </div>
                                </div>

                                <div class="clear">
                                    <hr class="bold">
                                </div>

                                <!-- Topics -->
                                {#if control.topic_tags}
                                <div>
                                    <h3 class="title is-5">Topics</h3>
                                    <div class="tags">
                                    {#each control.topic_tags as tag}
                                        <span class="tag is-medium pointer" on:click={() => toggleTopicTagFilter(tag)} class:is-info={topicTagFilter.includes(tag)}>{tag}</span>
                                    {/each}
                                    </div>
                                </div>
                                {/if}

                                <!-- References
                                <div>
                                    <h3 class="title is-5">References</h3>
                                    <div class="tags">
                                    {#each control.references as reference}
                                        <span class="tag">{reference.framework} {reference.control}</span>
                                    {/each}
                                    </div>
                                </div>

                                <hr>
                                -->

                                <!-- Guidance-->
                                {#if control.guidance}
                                <div class="guidance_container">
                                    <input type="checkbox" class="collapse" id="guidance_{control.id}">
                                    <label for="guidance_{control.id}" class="title is-5">Guidance</label>
                                    <div class="collapsible guidance">
                                        {@html clean(control.guidance)}
                                    </div>
                                </div>
                                {/if}
                            </div>

                            <div class="column">
                                <!-- Notes-->
                                <div>
                                    {#if assessment.previous_control_notes.notes && assessment.previous_control_notes.notes[control.id]}
                                        <PreviousNotes notes={assessment.previous_control_notes.notes[control.id]} collapsed={false} />
                                    {/if}
                                </div>

                            </div>
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
    </div>
</div>

<Popup title="Notepad (saved)" width={400} bind:visible={scratchPadVisible} id="scratchpad">
    <!--<textarea style="width: 100%; height: 100%; resize: none; border: none; outline: none;" bind:value="{assessment.scratch_notes[$session.user.email].text}" on:change={saveScratchNotes}></textarea>-->
    <Notepad bind:visible={scratchPadVisible} {assessment} />
</Popup>

<button class="button" class:is-hidden={scratchPadVisible} id="scratchpad-button" on:click={() => scratchPadVisible = !scratchPadVisible} title="Show Notepad">
    <Fa icon={faNoteSticky} />
</button>

<!--
<button class="button" class:is-primary={chatNotify} class:is-hidden={chatWindowVisible} id="chat-button" on:click={() => chatWindowVisible = true}>
    &#128489;
</button>
<Popup title="Chat (Not Saved)" width={400} bind:visible={chatWindowVisible} id="chat" notify={chatNotify}>
    <Chat room={params.id} bind:unread={chatNotify} on:popout={() => chatWindowVisible = false}></Chat>
</Popup>
-->
</Loader>


<div class="cover" class:active={selectedControl} on:click|self={()=> selectedControl = undefined}>
    <div class="evidence card">
        {#if selectedControl}
            <h1 class="title is-4">
                {selectedControl.control_number}
                <span class="delete is-pulled-right" aria-label="close" on:click={()=> selectedControl = undefined}></span>
            </h1>
            <!--
                <form on:submit|preventDefault={uploadEvidence}>
                    <input type="hidden" name="control_id" value="{selectedControl.id}">
                    <div class="field">
                        <label class="label" for="evidence_file">Evidence File</label>
                        <div class="file">
                            <label class="file-label">
                                <input class="file-input" type="file" name="evidence_file" on:change={(evt) => evt.target.nextElementSibling.nextElementSibling.innerText = evt.target.files[0].name}>
                                <span class="file-cta">
                                    <span class="file-icon">
                                        <i class="fas fa-upload">📁</i>
                                    </span>
                                    <span class="file-label">
                                        Add File
                                    </span>
                                </span>
                                <span class="file-name is-fullwidth">
                                    Select a file...
                                </span>
                            </label>
                        </div>
                    </div>
                    <div class="field">
                        <label class="label" for="evidence_escription">Evidence Description</label>
                        <div class="control">
                            <textarea class="textarea" id="evidence_description" placeholder="Evidence Description"></textarea>
                        </div>
                    </div>
                    <div class="buttons is-right">
                        <button class="button">Add Evidence</button>
                    </div>
                </form>
            -->
            <h2 class="title is-5">Manage Evidence</h2>
            {#if assessment.evidence[selectedControl.id]}
                {#each assessment.evidence[selectedControl.id] as attachment}
                    <div class="card">
                        <div class="card-content">
                            <p class="title is-6">{attachment.filename || "No File Attached"}</p>
                            <p>{attachment.description}</p>
                        </div>
                        <div class="card-footer">
                            <span class="card-footer-item link" on:click|stopPropagation={() => deleteEvidence(attachment)}>Delete</span>
                            <!--
                            {#if attachment.filename}
                                <span class="card-footer-item link" on:click={() => assessment_model.downloadEvidence(attachment)}>Download</span>
                            {/if}
                            -->
                        </div>
                    </div>
                    <hr>
                {/each}
            {/if}
        {/if}
    </div>
</div>

<style>
    .cover.active {
        visibility: visible;
    }
    .cover {
        visibility: hidden;
        height: 100vh;
        width: 100vw;
        position:fixed;
        top: 0;
        left: 0;
        background: rgba(250,250,250,0.5);
        z-index: 900;
    }

    .evidence {
        position: absolute;
        top: 0;
        right: -33%;
        width: 33%;
        background: white;
        padding: 60px 20px;
        height: 100vh;
        transition: .5s;
        overflow: auto;
    }

    .cover.active .evidence {
        right: 0;
    }

	.column {
        overflow: auto;
	}

	p {
        font-size: 14pt;
	}

    .columns {
        margin: 0;
        padding: 0;
    }

    .guidance {
        white-space: pre-wrap;
    }

    .chat-window {
        position: fixed;
        left: calc(25% + 25px);
        bottom: 0;
        width: 500px;
        z-index: 10;
        display: none;
    }

    .control-text {
        white-space: pre-wrap;
    }

    .guidance :global( p ) {
        margin-bottom: -10px !important;
    }

    #scratchpad-button {
        position: fixed;
        top: 50px;
        right: 50px;
        border-radius: 500px;
        width: 50px;
        height: 50px;
        text-align: center;
        line-height: 50px;
        padding: 0;
        margin: 0;
        cursor: pointer;
    }

    #chat-button {
        position: fixed;
        top: 50px;
        right: 125px;
        border-radius: 500px;
        width: 50px;
        height: 50px;
        text-align: center;
        line-height: 50px;
        padding: 0;
        margin: 0;
        cursor: pointer;
    }
</style>